import React from "react";
import {useTranslation} from "@Translation/hooks/useTranslation";
import Row from "@Containers/components/Row";
import {isExpired} from "@MyTicket/features/ticketDetails/functions/isExpired";
import useManageBookingModal from "@UserModals/features/manageBookings/hooks/useManageBookingModal";
import useRebookModal from "@UserModals/features/manageBookings/hooks/useRebookModal";
import ModalHeader from "@UserModals/components/ModalHeader";
import ManageBookingModalBody from "@UserModals/features/manageBookings/components/ManageBookingModalBody";
import ModalCloseButton from "@UserModals/components/ModalCloseButton";
import ManageBookingActionButton from "@UserModals/features/manageBookings/components/ManageBookingActionButton";
import ModalDataType from "@UserModals/types/ModalData.type";

interface ManageBookingModalProps {
    closeModal: () => void,
    modalData: ModalDataType,
}

function ManageBookingModal({closeModal, modalData}: ManageBookingModalProps) {
    const {
        ticketId,
        singleRoute: {
            companyId,
            departureDate,
            departureTime,
        },
        ancillariesData,
    } = modalData

    const isFirstLegExpired = isExpired(new Date(`${departureDate}T${departureTime}`))
    const isOneCompanyReturnTicket = companyId === modalData?.returnRoute.companyId
    const manageTicketLabel = useTranslation("my_profile.my_tickets.booking_modal.manage_ticket")

    const {
        radioButtonLabels,
        option,
        cancelTicketSelected,
    } = useManageBookingModal({modalData})

    const {
        handleTicketRebook,
        loading,
        voucher,
        resetApiState,
        successfulRebook,
        unsuccessfulRebook,
    } = useRebookModal(ticketId, closeModal)

    return (
        <>
            <ModalHeader
                modalData={modalData}
                title={`${manageTicketLabel} `}
            />
            <ManageBookingModalBody
                loadingData={loading}
                isFirstLegExpired={isFirstLegExpired}
                isOneCompanyReturnTicket={isOneCompanyReturnTicket}
                successfulRebook={successfulRebook}
                unsuccessfulRebook={unsuccessfulRebook}
                voucher={voucher}
                radioButtonLabels={radioButtonLabels}
                option={option}
                hasPremiumSupport={ancillariesData?.premiumSupport?.isEnabled ?? false}
            />
            {!loading && (
                <Row className="button-wrapper">
                    <ModalCloseButton
                        closeModal={closeModal}
                        additionalEffect={resetApiState}
                    />
                    <ManageBookingActionButton
                        cancelTicketOptionSelected={cancelTicketSelected}
                        unsuccessfulRebook={unsuccessfulRebook}
                        successfulRebook={successfulRebook}
                        isOneCompanyReturnTicket={isOneCompanyReturnTicket}
                        isFirstLegExpired={isFirstLegExpired}
                        resetApiState={resetApiState}
                        handleTicketRebook={handleTicketRebook}
                    />
                </Row>
            )}
        </>
    );
}

export default ManageBookingModal